import { useState } from "react";
import { Link } from "react-router-dom";
import { SpeakerSlash, SpeakerHigh, Play } from "@phosphor-icons/react";
import { kickRepaint } from "@/lib/repaint";

const HERO_IFRAME = "rv-onair-iframe";

const heroSrc = (url) => {
  const base = url.split("?")[0];
  return `${base}?autoplay=1&mute=1&controls=0&playsinline=1&rel=0&modestbranding=1&enablejsapi=1`;
};

const command = (func) =>
  document.getElementById(HERO_IFRAME)?.contentWindow?.postMessage(JSON.stringify({ event: "command", func, args: [] }), "*");

// "On-Air" hero: the top live channel auto-plays muted behind the Live TV title.
export const OnAirHero = ({ stream, liveCount }) => {
  const [muted, setMuted] = useState(true);
  const toggleSound = () => {
    command(muted ? "unMute" : "mute");
    if (muted) command("playVideo");
    setMuted(!muted);
  };

  return (
    <section data-testid="onair-hero" className="relative overflow-hidden border-b border-white/10 bg-black">
      <div className="relative w-full overflow-hidden" style={{ height: "min(56.25vw, 78vh)", minHeight: "320px" }}>
        {stream ? (
          <iframe
            key={stream.video_id}
            id={HERO_IFRAME}
            data-testid="onair-iframe"
            src={heroSrc(stream.monetized_embed_url)}
            title={stream.title}
            allow="autoplay; encrypted-media; picture-in-picture"
            tabIndex={-1}
            onLoad={(e) => kickRepaint(e.currentTarget)}
            className="rv-gpu rv-onair-frame pointer-events-none absolute border-0"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-crimson/20 via-black to-black" />
        )}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#050505] via-black/30 to-black/40" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-black/80 via-black/20 to-transparent" />

        <div className="absolute inset-x-0 bottom-0 mx-auto max-w-[1600px] min-[2200px]:max-w-[2200px] px-5 pb-10 sm:px-8 sm:pb-14">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 bg-crimson px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-white">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> On Air
            </span>
            <span data-testid="live-count" className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60">
              {liveCount} channel{liveCount === 1 ? "" : "s"} streaming
            </span>
          </div>
          <h1 className="mt-4 font-serif text-4xl tracking-tighter text-[#EDEDED] sm:text-5xl lg:text-6xl">
            Live <span className="text-crimson">TV</span>
          </h1>
          {stream && (
            <p data-testid="onair-title" className="mt-3 max-w-2xl line-clamp-2 text-base text-white/80">
              Now playing: <span className="text-white">{stream.title}</span>
              {stream.channel_name && <span className="text-white/45"> · {stream.channel_name}</span>}
            </p>
          )}
          {stream && (
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to={`/watch/${stream.video_id}?autoplay=1`}
                data-testid="onair-watch-live"
                className="flex items-center gap-2 bg-crimson px-6 py-3 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-amber hover:text-black focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
              >
                <Play size={14} weight="fill" /> Watch Live
              </Link>
              <button
                type="button"
                onClick={toggleSound}
                data-testid="onair-unmute"
                className="flex items-center gap-2 border border-white/30 bg-black/40 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-white backdrop-blur-md transition-colors duration-300 hover:border-amber hover:text-amber focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
              >
                {muted ? <SpeakerSlash size={16} /> : <SpeakerHigh size={16} />}
                {muted ? "Click to Unmute" : "Mute"}
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
